'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { LuArrowUpRight } from 'react-icons/lu';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { IoMdPause, IoMdPlay } from 'react-icons/io';
import { useState, useCallback, useEffect } from 'react';
import { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

export function Hero({
  subtitle,
  title,
  description,
  heroGallery,
}: {
  subtitle: string;
  title: string;
  description: string;
  heroGallery: TDynamicData[];
}) {
  const slides = heroGallery[0]?.fields[0]?.images ?? [];
  const [isPaused, setIsPaused] = useState(false);
  const [firstImageLoaded, setFirstImageLoaded] = useState(false);
  const [secondaryImagesReady, setSecondaryImagesReady] = useState(false);
  const enableAutoPlay = useBreakpoint('md');

  // Hidden photographs must not compete with the first visible image. Keep their
  // slide geometry in SSR, then preload them after the critical image has painted.
  useEffect(() => {
    if (!firstImageLoaded) return;
    let timer: ReturnType<typeof setTimeout>;
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        timer = setTimeout(() => setSecondaryImagesReady(true), 500);
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
      clearTimeout(timer);
    };
  }, [firstImageLoaded]);

  const { t } = useTranslations();

  const togglePlayPause = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);

  return (
    <section className={cn('custom-section', 'pb-6 pt-12 sm:pb-8 lg:pb-12')}>
      <div className={cn('custom-container', 'grid grid-cols-1 gap-12 md:grid-cols-2 md:gap-6')}>
        <div className="col-span-1 flex w-full flex-col items-start justify-start gap-6 md:justify-between">
          <p className="text-sm font-medium tracking-tight text-primary">{subtitle}</p>
          <div className="flex w-full flex-col items-start justify-start gap-6">
            <Heading tag="h1" title={title} />
            <Paragraph innerHtml={description} size="lg" className="w-full max-w-[500px]" />
            <div className="flex w-full items-center justify-start gap-2 sm:gap-3">
              <Button asChild className="w-full flex-1 whitespace-nowrap sm:w-fit sm:flex-grow-0">
                <Link href={'/kategorie'} prefetchOnHover>
                  {t('text3755')} <LuArrowUpRight size={20} />
                </Link>
              </Button>
              <Button
                asChild
                variant={'outline'}
                className="w-full flex-1 whitespace-nowrap sm:w-fit sm:flex-grow-0"
              >
                <Link href={'#o-nas'}>{t('text1242')}</Link>
              </Button>
            </div>
          </div>
          <div className="hidden h-8 w-full md:block" />
          {/* <div className="flex w-fit items-center justify-start gap-3 rounded-[20px] border p-4 sm:p-5 lg:p-6">
            <Image
              src={'/images/icons/google.svg'}
              alt="Google"
              width={0}
              height={0}
              className="max-h-6 min-h-6 min-w-6 max-w-6 object-contain sm:max-h-8 sm:min-h-8 sm:min-w-8 sm:max-w-8 lg:max-h-10 lg:min-h-10 lg:min-w-10 lg:max-w-10"
            />
            <div className="h-6 w-[1px] bg-neutral-200 sm:h-8 lg:h-[52px]" />
            <Heading title="4.8" className={{ title: 'tracking-tighter' }} color="black" />
            <div className="flex items-center justify-start gap-[6px]">
              <FaStar className="max-h-6 min-h-6 min-w-6 max-w-6 fill-primary text-primary" />
              <FaStar className="hidden max-h-6 min-h-6 min-w-6 max-w-6 fill-primary text-primary sm:block md:hidden xl:block" />
              <FaStar className="hidden max-h-6 min-h-6 min-w-6 max-w-6 fill-primary text-primary sm:block md:hidden xl:block" />
              <FaStar className="hidden max-h-6 min-h-6 min-w-6 max-w-6 fill-primary text-primary sm:block md:hidden xl:block" />
              <FaRegStarHalfStroke className="hidden max-h-7 min-h-7 min-w-7 max-w-7 text-primary sm:block md:hidden xl:block" />
            </div>
            <div className="max-h-1 min-h-1 min-w-1 max-w-1 rounded-full bg-black" />
            <p className="whitespace-nowrap text-lg tracking-tight text-black sm:text-xl lg:text-2xl">
              (180 {t('text3513')})
            </p>
          </div> */}
        </div>
        <div
          onPointerDown={() => setSecondaryImagesReady(true)}
          onFocusCapture={() => setSecondaryImagesReady(true)}
          className="relative col-span-1 flex h-[364px] w-full items-end justify-end overflow-hidden rounded-[20px] bg-primary/10 sm:h-[500px] lg:h-[700px]"
        >
          <div className="absolute bottom-0 left-1/2 z-10 flex w-full -translate-x-1/2 items-end justify-between p-4 sm:p-5 lg:p-6">
            <button
              aria-label={isPaused ? 'Play slideshow' : 'Pause slideshow'}
              onClick={togglePlayPause}
              className="hidden h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-white transition-colors hover:bg-white/80 md:flex"
            >
              {isPaused ? (
                <IoMdPlay size={20} className="text-black" />
              ) : (
                <IoMdPause size={20} className="text-black" />
              )}
            </button>
            <div className="flex w-fit items-center justify-end gap-3 rounded-[20px] bg-white/25 p-4 backdrop-blur-[4px] sm:p-5 lg:p-6">
              <Image
                src={'/images/icons/25.svg'}
                alt="25"
                width={56}
                height={56}
                className="shrink-0 object-contain"
              />
              <p className="text-lg font-medium tracking-tight text-white sm:text-xl lg:text-2xl">
                Rokov
                <br />
                skúseností
              </p>
            </div>
          </div>
          <Carousel
            enableInterval={enableAutoPlay && secondaryImagesReady}
            enableLoop
            intervalLength={5000}
            paused={isPaused}
            className={{
              wrapper: 'h-full w-full',
              main: 'h-full w-full',
              slide: 'relative h-full w-full',
              container: 'h-full w-full',
            }}
            slides={slides.map((slide, index) =>
              index === 0 || secondaryImagesReady ? (
                <Image
                  src={slide.url}
                  alt={resolveImageAlt(slide, slide?.url)}
                  key={slide.url}
                  fill
                  priority={index === 0}
                  fetchPriority={index === 0 ? 'high' : 'low'}
                  onLoad={index === 0 ? () => setFirstImageLoaded(true) : undefined}
                  sizes="(min-width: 1440px) 688px, (min-width: 768px) calc(50vw - 36px), calc(100vw - 32px)"
                  className="absolute inset-0 z-0 h-full w-full object-cover object-center"
                />
              ) : (
                <div
                  key={slide.url}
                  className="absolute inset-0 bg-primary/10"
                  aria-hidden="true"
                />
              ),
            )}
          />
        </div>
      </div>
    </section>
  );
}
